"use client";

import React from "react";
import styles from "./token-selector.module.scss";
import classNames from "classnames";
import { useBridge } from "@/features/bridge/providers/bridge.provider";
import { numericFormatter } from "react-number-format";
import MultiSelectionWindow from "@/features/bridge/components/token-selector/selector-interface/multi-selection-window";
import NftSelectionWindow from "@/features/bridge/components/token-selector/selector-interface/nft-selection-window";
import StandardTokenWindow from "@/features/bridge/components/token-selector/selector-interface/standard-token/standard-token-window";
import { useMultiTokenManager } from "@/features/bridge/stores/multi-token-manager";
import { useNftTokenManager } from "@/features/bridge/stores/nft-token-manager";
import { useStandardTokenManager } from "@/features/bridge/stores/standard-token-manager.store";
import { useTokenManagerStore } from "@/features/bridge/stores/token-type.store";
import { useTokenWindowControls } from "@/features/bridge/stores/token-window.store";
import { TokenType } from "@/features/bridge/types/token-selector.types";
import { useConnectModal } from "@rainbow-me/rainbowkit";

const TokenSelectors = () => {
  const showStandardTokenSelector = false;
  const showNftSelector = false;

  const {
    bridgeControls: { account },
    validationManager: {
      isBothChainsSelected,
      isSameChains,
      isWalletConnected,
    },
  } = useBridge();

  const { openConnectModal } = useConnectModal();

  const { currentlySelectedTokenType, setCurrentlySelectedTokenType } =
    useTokenManagerStore();

  const { setActiveTokenWindowType } = useTokenWindowControls();

  const { selectedToken, tokenAmount, clearStandardTokenState } =
    useStandardTokenManager();

  const { getNftCount, clearNftTokenState, activeNftCollection } =
    useNftTokenManager((state) => ({
      activeNftCollection: state.activeCollection,
      getNftCount: state.getAllSelectionsCount,
      clearNftTokenState: state.deselectAllTokens,
    }));
  const { clearMultiTokenState, getMultiCount, activeMultiCollection } =
    useMultiTokenManager((state) => ({
      clearMultiTokenState: state.deselectAllCollections,
      getMultiCount: state.getAllSelectionsCount,
      activeMultiCollection: state.activeCollection,
    }));

  const nftCount = getNftCount();

  const isValidForSelection = (): boolean => {
    if (!isWalletConnected()) {
      openConnectModal?.();
      return false;
    }
    if (isBothChainsSelected() && !isSameChains()) return true;
    else return false;
  };

  const handleSelectNft = () => {
    if (!isValidForSelection()) return;
    clearStandardTokenState();
    clearMultiTokenState();
    setCurrentlySelectedTokenType(TokenType.NFT);
    setActiveTokenWindowType(TokenType.NFT);
  };

  const handleSelectMulti = () => {
    if (!isValidForSelection()) return;
    clearNftTokenState();
    clearStandardTokenState();
    setCurrentlySelectedTokenType(TokenType.MULTI);
    setActiveTokenWindowType(TokenType.MULTI);
  };

  const handleSelectToken = () => {
    if (!isValidForSelection()) return;
    clearNftTokenState();
    clearMultiTokenState();
    setCurrentlySelectedTokenType(TokenType.STANDARD);
    setActiveTokenWindowType(TokenType.STANDARD);
  };

  const formatNumber = (amount: number) => {
    return numericFormatter(amount.toString(), {
      thousandSeparator: ",",
    });
  };

  return (
    <div
      className={classNames(
        styles.tokenSelectorContainer,
        !account.isConnected && styles.disabled,
      )}
    >
      {showStandardTokenSelector && (
        <button
          className={classNames(
            styles.tokenBtn,
            currentlySelectedTokenType === TokenType.STANDARD && styles.active,
          )}
          onClick={handleSelectToken}
        >
          <div className={styles.tokenDesc}>
            <p>Token</p>
          </div>
          {selectedToken && (
            <span className={styles.tokenCount}>
              <b>x{formatNumber(tokenAmount)}</b>{" "}
              <span>${selectedToken?.symbol}</span>
            </span>
          )}
        </button>
      )}
      {showNftSelector && (
        <button
          className={classNames(
            styles.tokenBtn,
            currentlySelectedTokenType === TokenType.NFT && styles.active,
          )}
          onClick={handleSelectNft}
        >
          <div className={styles.tokenDesc}>
            <p>NFTs</p>
          </div>
          {!!nftCount && (
            <span className={styles.tokenCount}>
              <b>x{getNftCount()}</b> in {activeNftCollection}
            </span>
          )}
        </button>
      )}
      <button
        className={classNames(
          styles.tokenBtn,
          currentlySelectedTokenType === TokenType.MULTI && styles.active,
        )}
        onClick={handleSelectMulti}
      >
        <div className={styles.tokenDesc}>
          <p>Multi-Tokens</p>
        </div>
        {!!getMultiCount() && (
          <span className={styles.tokenCount}>
            <b>x{getMultiCount()}</b> in {activeMultiCollection}
          </span>
        )}
      </button>
      {showNftSelector && <NftSelectionWindow />}
      <MultiSelectionWindow />
      {showStandardTokenSelector && <StandardTokenWindow />}
    </div>
  );
};

export default TokenSelectors;
